import {z} from 'zod';

export const smtpCredentialsSchema = z.object({
  host: z.string().min(1, 'Host is required'),
  port: z.number().int().min(1).max(65535),
  user: z.string().min(1, 'User is required'),
  password: z.string().min(1, 'Password is required'),
  encryption: z.enum(['ssl', 'tls', 'ssl/tls', 'starttls']).nullable(),
  defaultEmail: z.string().email('Invalid email format'),
});

export const emailConfigRequestBodySchema = z.object({
  enabled: z.boolean().nullable().optional(),
  credentials: smtpCredentialsSchema.nullable().optional(),
});

export type SmtpCredentials = z.infer<typeof smtpCredentialsSchema>;
export type EmailConfigRequestBody = z.infer<typeof emailConfigRequestBodySchema>;

export type PatchEmailConfigApiResponse = /** status 204  */ void;

export const PatchEmailConfigApiArgSchema = z.object({
  emailConfigRequestBody: emailConfigRequestBodySchema,
});

export type PatchEmailConfigApiArg = z.infer<typeof PatchEmailConfigApiArgSchema>;

// WhatsApp Configuration Types
export const whatsAppCredentialsSchema = z.object({
  appId: z.string().min(1, 'App ID is required'),
  appSecret: z.string().min(1, 'App Secret is required'),
  wabaId: z.string().min(1, 'WABA ID is required'),
  phoneNumber: z.string().min(1, 'Phone Number is required'),
  permanentToken: z.string().min(1, 'Permanent Token is required'),
  webhookVerificationToken: z.string().min(1, 'Webhook Verification Token is required'),
});

export const whatsAppConfigResponseBodySchema = z.object({
  enabled: z.boolean(),
  credentials: whatsAppCredentialsSchema.nullable(),
});

export const whatsAppConfigRequestBodySchema = z.object({
  enabled: z.boolean().nullable().optional(),
  credentials: whatsAppCredentialsSchema.nullable().optional(),
});

export type WhatsAppCredentials = z.infer<typeof whatsAppCredentialsSchema>;
export type WhatsAppConfigResponseBody = z.infer<typeof whatsAppConfigResponseBodySchema>;
export type WhatsAppConfigRequestBody = z.infer<typeof whatsAppConfigRequestBodySchema>;

export type GetWhatsAppConfigApiResponse = /** status 200  */ WhatsAppConfigResponseBody;
export type PatchWhatsAppConfigApiResponse = unknown;

export const GetWhatsAppConfigApiArgSchema = z.void();

export const PatchWhatsAppConfigApiArgSchema = z.object({
  whatsAppConfigRequestBody: whatsAppConfigRequestBodySchema,
});

export type GetWhatsAppConfigApiArg = z.infer<typeof GetWhatsAppConfigApiArgSchema>;
export type PatchWhatsAppConfigApiArg = z.infer<typeof PatchWhatsAppConfigApiArgSchema>;

export type InviteUserApiResponse = /** status 201  */ {id: string};

export type InviteUserApiArg = {
  inviteUserRequestBody: InviteUserRequestBody;
};

export const InviteUserRequestBodySchema = z.object({
  email: z.string().email('Invalid email format'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roles: z.array(z.string()).min(1, 'Roles are required'),
  defaultBranchId: z.string().min(1, 'Default branch id is required'),
  branchIds: z.array(z.string()).min(1, 'Branch ids are required'),
});

export type InviteUserRequestBody = z.infer<typeof InviteUserRequestBodySchema>;

export const PhotoCheckerModeSchema = z.enum(['manual', 'auto']);
export type PhotoCheckerMode = z.infer<typeof PhotoCheckerModeSchema>;

export const PhotoCheckerConfigResponseBodySchema = z.object({
  enabled: z.boolean(),
  mode: PhotoCheckerModeSchema,
  automaticModeEnabledAt: z.string().nullable(),
});

export const PhotoCheckerConfigRequestBodySchema = z.object({
  enabled: z.boolean().nullable().optional(),
  mode: PhotoCheckerModeSchema.nullable().optional(),
});

export type PhotoCheckerConfigResponseBody = z.infer<typeof PhotoCheckerConfigResponseBodySchema>;
export type PhotoCheckerConfigRequestBody = z.infer<typeof PhotoCheckerConfigRequestBodySchema>;

export type GetPhotoCheckerConfigApiResponse = PhotoCheckerConfigResponseBody;
export type PatchPhotoCheckerConfigApiArg = PhotoCheckerConfigRequestBody;
export type PatchPhotoCheckerConfigApiResponse = void;

export const AutotracerPlConfigResponseBodySchema = z.object({
  enabled: z.boolean(),
});

export const AutotracerPlConfigRequestBodySchema = z.object({
  enabled: z.boolean(),
});

export type AutotracerPlConfigResponseBody = z.infer<typeof AutotracerPlConfigResponseBodySchema>;
export type AutotracerPlConfigRequestBody = z.infer<typeof AutotracerPlConfigRequestBodySchema>;

export type GetAutotracerPlConfigApiResponse = AutotracerPlConfigResponseBody;
export type PatchAutotracerPlConfigApiArg = AutotracerPlConfigRequestBody;
export type PatchAutotracerPlConfigApiResponse = void;
